import {
  Replicache,
  TEST_LICENSE_KEY,
  type PullResponseV1,
  type Puller,
  type Pusher,
} from "replicache";
import { makeMutators, type AppMutators } from "./mutators";

// Bumped to "2" when E.3 added ownerId to Group/Item.
// Bumped to "3" to recover devices stuck in the "shared group is empty"
// state — the previous SCHEMA_VERSION=2 rollout wiped IDB but didn't
// reset the server's per-client-group share_pull_state, so the first
// pull cleared and then skipped re-emitting items it thought were
// already delivered.  Pull now purges share_pull_state inside the
// since < 0 branch (see api/src/replicache/pull.ts), and bumping the
// schema again forces every device to take that path one more time.
// Bumped to "4" alongside the sync-integrity hash work: every row type
// gained a `version` field, and pre-rollout cached rows lack it.  The
// bump forces one full re-pull so cached rows pick up `version` and the
// hash comparison has well-defined inputs from the very first check.
const SCHEMA_VERSION = "4";

export type AppReplicache = Replicache<AppMutators>;

// Build a Replicache instance for a particular user, wired to our authed
// pull/push endpoints.  `getAccessToken` is called every request so that an
// access token rotated by the background refresher is picked up immediately
// without needing to re-construct the instance.

export function createReplicache(opts: {
  userId: string;
  getAccessToken: () => string | null;
}): AppReplicache {
  const authedJsonFetch = async (
    url: string,
    body: unknown,
  ): Promise<{ status: number; body: unknown }> => {
    const access = opts.getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (access) headers.Authorization = `Bearer ${access}`;
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    const data = res.status === 204 ? null : await res.json().catch(() => null);
    return { status: res.status, body: data };
  };

  // Forward-ref so the puller (constructed before `rep`) can read the
  // client's local state when computing the integrity hash.  First pull
  // fires before this is set — fine, the request just goes out without a
  // hash and the server treats that as "skip check this round".
  let repRef: AppReplicache | null = null;

  const puller: Puller = async (request) => {
    const augmented = await augmentPullRequest(request, repRef);
    const { status, body } = await authedJsonFetch(
      "/api/replicache/pull",
      augmented,
    );
    if (status !== 200) {
      return {
        httpRequestInfo: {
          httpStatusCode: status,
          errorMessage: `pull ${status}`,
        },
      };
    }
    return {
      response: body as PullResponseV1,
      httpRequestInfo: { httpStatusCode: 200, errorMessage: "" },
    };
  };

  const pusher: Pusher = async (request) => {
    const { status } = await authedJsonFetch("/api/replicache/push", request);
    return {
      httpRequestInfo: {
        httpStatusCode: status,
        errorMessage: status === 200 ? "" : `push ${status}`,
      },
    };
  };

  const rep = new Replicache({
    // Per-user store name keeps multiple users on one device isolated.
    name: opts.userId,
    licenseKey: TEST_LICENSE_KEY,
    schemaVersion: SCHEMA_VERSION,
    mutators: makeMutators(opts.userId),
    puller,
    pusher,
    pullInterval: 60_000, // spec §6.3
    pushDelay: 200, // spec §6.3: debounced 200ms
  });
  repRef = rep;
  return rep;
}

// ---------------------------------------------------------------------------
// Sync-integrity hash (client side)
// ---------------------------------------------------------------------------

/** Augment Replicache's outgoing pull request with `clientStateHash` and
 *  `pendingCount`.  The server compares the hash against its own
 *  computation of "what the client should have" — see api/src/replicache/
 *  pull.ts > checkClientDrift for the contract.
 *
 *  When pending mutations exist the local state is "server snapshot +
 *  optimistic writes", which doesn't match the server's confirmed view.
 *  We send `pendingCount` so the server can skip the check; the hash is
 *  omitted so a stray comparison can't accidentally fire.  Once push
 *  drains and the next quiescent pull arrives, the hash gets sent. */
async function augmentPullRequest(
  request: unknown,
  rep: AppReplicache | null,
): Promise<unknown> {
  if (!rep || typeof request !== "object" || request === null) return request;
  try {
    const pending = await rep.experimentalPendingMutations();
    if (pending.length > 0) {
      return { ...request, pendingCount: pending.length };
    }
    const hash = await computeClientStateHash(rep);
    return { ...request, clientStateHash: hash, pendingCount: 0 };
  } catch (err) {
    // Don't block a pull on the integrity check — drop the augmentation
    // and let the pull proceed normally.  The server will treat the
    // missing hash as "skip check this round" and we'll try again on
    // the next pull.
    console.warn("[replicache pull] hash augmentation failed", err);
    return request;
  }
}

async function computeClientStateHash(rep: AppReplicache): Promise<string> {
  const lines = await rep.query(async (tx) => {
    const out: string[] = [];
    for await (const [key, value] of tx.scan().entries()) {
      const version =
        value !== null &&
        typeof value === "object" &&
        typeof (value as { version?: unknown }).version === "number"
          ? (value as { version: number }).version
          : 0;
      out.push(`${key}|${version}`);
    }
    return out;
  });
  lines.sort();
  const bytes = new TextEncoder().encode(lines.join("\n"));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
